import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import uniqid from 'uniqid';

import {
  MODAL_TYPES,
  WINDOW_EVENTS,
} from 'src/components/RepositoryItemActions/constants';
import { RepositoryItem } from 'src/modules/repository/type';
import {
  TRIAL_FEATURES,
  useTrialPaywall,
} from 'src/modules/trial-paywall';
import { useFetchDestinations } from '../components/AudienceActivationModal/api/hooks/useFetchDestinations';
import { useWindowEvents } from '../hooks/useWindowEvents';
import { DestinationMap, SelectedDestinationMap } from '../types';

export interface AudienceActivationContextData {
  runId: string;
  modalType: keyof typeof MODAL_TYPES;
  analyseData: any;
  showActivationModal: boolean;
  isFetchingDestinations: boolean;
  destinations: DestinationMap;
  selectedDestinations: SelectedDestinationMap;
  repositoryItem: RepositoryItem;
  setSelectedDestinations: (destinations: SelectedDestinationMap) => void;
  openModal: () => void;
  closeModal: () => void;
  setModalType: (type: keyof typeof MODAL_TYPES) => void;
  setRepositoryItem: (repositoryItem: RepositoryItem) => void;
}

export const AudienceActivationContext =
  createContext<AudienceActivationContextData | null>(null);

interface AudienceActivationProviderProps {
  children: ReactNode;
}

export const AudienceActivationProvider = ({
  children,
}: AudienceActivationProviderProps) => {
  const { guardFeature } = useTrialPaywall();
  const [runId, setRunId] = useState<string>('');
  const [modalType, setModalType] = useState<keyof typeof MODAL_TYPES>(
    MODAL_TYPES.OPEN_CAMPAIGN
  );
  const [showActivationModal, setShowActivationModal] =
    useState<boolean>(false);
  const [repositoryItem, setRepositoryItem] = useState<RepositoryItem>(null);
  const [destinations, setDestinations] = useState<DestinationMap>(new Map());
  const [selectedDestinations, setSelectedDestinations] =
    useState<SelectedDestinationMap>(new Map());
  const [analyseData, setAnalyseData] = useState<any>(null);

  const { data, isLoading: isFetchingDestinations } =
    useFetchDestinations(showActivationModal);

  useEffect(() => {
    if (Array.isArray(data) && data.length > 0) {
      setDestinations(
        new Map(
          data.map((item) => [item.destination, { ...item, isSelected: false }])
        )
      );
    }
  }, [data]);

  useEffect(() => {
    if (!repositoryItem?.campaign) return;

    const selectedDest = repositoryItem?.campaign?.selectedDestinations?.map(
      (item) => ({
        ...item,
        isSelected: false,
        id: uniqid(),
        image: destinations.get(item.destination)?.image,
      })
    );

    const newDestinations = new Map(
      selectedDest.map((item) => [item.id, item])
    );
    setSelectedDestinations(newDestinations);
    setRunId(repositoryItem.campaign.runId);
  }, [repositoryItem, destinations, setSelectedDestinations, setRunId]);

  const openModal = useCallback((type?: keyof typeof MODAL_TYPES) => {
    setModalType(type || MODAL_TYPES.NEW_CAMPAIGN);
    setShowActivationModal(true);
  }, []);

  const closeModal = useCallback(
    () => setShowActivationModal(false),
    [setShowActivationModal]
  );

  const handleOpenAudienceModal = useCallback(
    (event) => {
      if (!event.detail) return;

      guardFeature(TRIAL_FEATURES.ACTIVATION, () => {
        const { type, item, runId, analyseData } = event.detail;

        setShowActivationModal(true);
        setModalType(type);
        setRunId(runId);
        setAnalyseData(analyseData);
        setSelectedDestinations(new Map());

        if (item) setRepositoryItem({ ...item });
      });
    },
    [guardFeature]
  );

  useWindowEvents(
    WINDOW_EVENTS.OPEN_AUDIENCE_ACTIVATION_MODAL,
    handleOpenAudienceModal
  );

  return (
    <AudienceActivationContext.Provider
      value={{
        runId,
        modalType,
        analyseData,
        showActivationModal,
        destinations,
        isFetchingDestinations,
        selectedDestinations,
        repositoryItem,
        setSelectedDestinations,
        openModal,
        closeModal,
        setModalType,
        setRepositoryItem,
      }}
    >
      {children}
    </AudienceActivationContext.Provider>
  );
};

export const useAudienceActivationContext =
  (): AudienceActivationContextData => {
    const audienceActivationContext = useContext(AudienceActivationContext);

    if (!audienceActivationContext) {
      throw new Error('context must be used inside a provider');
    }

    return audienceActivationContext;
  };
